import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

interface HeartRateStats {
  average: number
  stdDev: number
  min: number
  max: number
  sampleCount: number
}

interface UseHeartRateStatsReturn {
  stats: HeartRateStats | null
  loading: boolean
  error: string | null
  refreshStats: () => Promise<void>
}

export const useHeartRateStats = (workoutId: string): UseHeartRateStatsReturn => {
  const [stats, setStats] = useState<HeartRateStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadHeartRateStats = useCallback(async () => {
    if (!workoutId) return

    try {
      setLoading(true)
      setError(null)

      // Fetch heart rate metrics for the workout
      const { data: metrics, error: metricsError } = await supabase
        .from('workout_metrics')
        .select('values')
        .eq('workout_id', workoutId)
        .eq('metric_name', 'heart_rate')
        .single()

      if (metricsError) {
        // If no data found, this is not an error - just no HR data available
        if (metricsError.code === 'PGRST116') {
          setStats(null)
          setLoading(false)
          return
        }
        throw new Error(metricsError.message)
      }

      if (!metrics || !metrics.values || metrics.values.length === 0) {
        setStats(null)
        setLoading(false)
        return
      }

      // Extract non-null heart rate values from the array
      const hrValues = metrics.values.filter((val: number | null) => val !== null) as number[]

      if (hrValues.length === 0) {
        setStats(null)
        setLoading(false)
        return
      }

      const n = hrValues.length

      // Calculate mean
      const mean = hrValues.reduce((sum, hr) => sum + hr, 0) / n

      // Calculate standard deviation
      const squaredDiffs = hrValues.map(hr => Math.pow(hr - mean, 2))
      const variance = squaredDiffs.reduce((sum, diff) => sum + diff, 0) / n
      const stdDev = Math.sqrt(variance)

      // Calculate min and max
      const min = Math.min(...hrValues)
      const max = Math.max(...hrValues)

      setStats({
        average: mean,
        stdDev: stdDev,
        min: min,
        max: max,
        sampleCount: n,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load heart rate statistics')
      console.error('Error loading heart rate statistics:', err)
    } finally {
      setLoading(false)
    }
  }, [workoutId])

  const refreshStats = useCallback(async () => {
    await loadHeartRateStats()
  }, [loadHeartRateStats])

  useEffect(() => {
    if (workoutId) {
      loadHeartRateStats()
    }
  }, [workoutId, loadHeartRateStats])

  return {
    stats,
    loading,
    error,
    refreshStats,
  }
}
